import type { Migration as SchemaMigration, CloudInstance, TargetProject } from '@schema';
import {
  MigrationStatus,
  MIGRATION_STATUS_TEXT,
  MIGRATION_STATUS_ICON
} from '@/common/constants/migration.constants';
import { IP_V4_REGEXP, IP_V6_REGEXP } from '@/common/utils/validate';
import { store } from '@/common/root';

export const resolveProjectDisplay = (projectId?: string, targetProject?: TargetProject) => {
  const id = targetProject?.id ?? projectId;
  const project = id ? store.getters['projects/getItemById'](id) : undefined;
  const domainId = targetProject?.domain_id ?? project?.domain_id;
  return {
    projectId: id ?? '',
    domainId: domainId ?? '',
    domain: targetProject?.domain_name ?? (domainId ? store.getters['domains/domains/getNameOrId'](domainId) : ''),
    project: targetProject?.name ?? project?.name ?? id ?? ''
  };
};

export interface MigrationNetworkInterface {
  mac: string;
  ipAddress: string;
  network: string;
  type: string;
}

export interface MigrationDisk {
  id: number;
  size: number;
  allocatedSize: number;
  backendIdentifier: string;
  format: string;
  guestDevice: string;
}

export interface MigrationSourceVM {
  vmId: string;
  name: string;
  hostname: string;
  uefi: boolean;
  state: string;
  osType: string;
  osDistro: string;
  osVersion: string;
  guestTools: string;
  cores: number;
  memory: number;
  networkInterfaces: MigrationNetworkInterface[];
  disks: MigrationDisk[];
}

export interface MigrationTargetMapping {
  networkMappings: {
    source: string;
    target: string;
  }[];
  storageDefault: string;
}

export interface MigrationSourceOptions {
  os: string;
  vCPU: number;
  ram: number;
  disksCount: number;
  disksSize: number;
  automaticallyEnableCBT: boolean;
  vixDiskLibCompatibility: string;
}

export interface MigrationTargetOptions {
  flavorName: string;
  diskBus: string;
  osDistro: string;
  setDhcp: boolean;
  preserveFixedIps: boolean;
  useFloatingIp: boolean;
  preserveFloatingIp: boolean;
  retainUserCredentials: boolean;
  deleteDiskOnTermination: boolean;
  allowLiveResize: boolean;
  machineType: string;
  keypairName: string;
  floatingIpPool: string;
}

export interface MigrationDeploymentOpts {
  cloneDisks: boolean;
  forceDeployment: boolean;
  osMorphing: boolean;
  saveSnapshot: boolean;
  start: boolean;
}

export interface MigrationVmName {
  vmId: string;
  sourceName: string;
  targetName: string;
}

export interface Migration {
  id: string;
  name: string;
  status: MigrationStatus;
  sourceCloudId: string;
  projectId: string;
  domainId: string;
  domain: string;
  project: string;
  createdAt: string;
  updatedAt: string;
  sourceOptions: MigrationSourceOptions;
  targetOptions: MigrationTargetOptions;
  sourceVMs: MigrationSourceVM[];
  targetMappings: MigrationTargetMapping;
  deploymentOpts: MigrationDeploymentOpts;
}

export type MappedMigration = Migration & {
  statusText: string;
  statusIcon: string;
};

const VM_STATE_LABELS: Record<string, string> = {
  poweredOn: 'Active',
  poweredOff: 'Stopped',
  suspended: 'Suspended',
  running: 'Active',
  stopped: 'Stopped',
  shutoff: 'Stopped',
  paused: 'Paused'
};

const firstIPAddress = (addresses: string[] | undefined): string => {
  if (!addresses?.length) return '';
  return addresses.find(ip => IP_V4_REGEXP.test(ip))
    ?? addresses.find(ip => IP_V6_REGEXP.test(ip))
    ?? addresses[0];
};

export const mapInstance = (v: CloudInstance): MigrationSourceVM => ({
  vmId: v.id,
  name: v.name ?? '',
  hostname: v.hostname ?? '',
  uefi: v.uefi ?? false,
  state: VM_STATE_LABELS[v.state ?? ''] ?? v.state ?? '',
  osType: v.os_type ?? '',
  osDistro: v.os_distro ?? '',
  osVersion: v.os_version ?? '',
  guestTools: v.guest_tools
    ? [v.guest_tools.state, v.guest_tools.version ? `v${v.guest_tools.version}` : ''].filter(Boolean).join(' ')
    : '',
  cores: v.num_cpu ?? 0,
  memory: (v.memory_mb ?? 0) * 1024 * 1024,
  networkInterfaces: (v.nics ?? []).map(n => ({
    mac: n.mac_address ?? '',
    ipAddress: firstIPAddress(n.ip_addresses),
    network: n.network_name ?? '',
    type: n.name ?? ''
  })),
  disks: (v.disks ?? []).map(d => ({
    id: parseInt(d.id ?? '0', 10) || 0,
    size: d.size_bytes ?? 0,
    allocatedSize: 0,
    backendIdentifier: d.storage_backend_identifier ?? '',
    format: d.format ?? '',
    guestDevice: d.path ?? d.unit_number?.toString() ?? ''
  }))
});

export const mapSourceOptions = (
  instances: CloudInstance[],
  sourceEnvironment: SchemaMigration['source_environment']
): MigrationSourceOptions => {
  const agg = instances.reduce(
    (acc, v) => ({
      vCPU: acc.vCPU + (v.num_cpu ?? 0),
      ram: acc.ram + (v.memory_mb ?? 0),
      disksCount: acc.disksCount + (v.disks?.length ?? 0),
      disksSize: acc.disksSize + (v.disks ?? []).reduce((ds, d) => ds + (d.size_bytes ?? 0), 0)
    }),
    { vCPU: 0, ram: 0, disksCount: 0, disksSize: 0 }
  );
  return {
    os: instances[0]?.os_type ?? '',
    vCPU: agg.vCPU,
    ram: agg.ram * 1024 * 1024,
    disksCount: agg.disksCount,
    disksSize: agg.disksSize,
    automaticallyEnableCBT: sourceEnvironment?.automatically_enable_cbt ?? false,
    vixDiskLibCompatibility: sourceEnvironment?.vixdisklib_compatibility_version ?? ''
  };
};

export const mapTargetOptions = (
  targetEnvironment: SchemaMigration['target_environment']
): MigrationTargetOptions => ({
  flavorName: targetEnvironment?.flavor_name ?? '',
  diskBus: targetEnvironment?.disk_bus ?? '',
  osDistro: targetEnvironment?.os_distro ?? '',
  setDhcp: targetEnvironment?.set_dhcp ?? false,
  preserveFixedIps: targetEnvironment?.preserve_fixed_ips ?? false,
  useFloatingIp: targetEnvironment?.use_floating_ip ?? false,
  preserveFloatingIp: targetEnvironment?.preserve_floating_ip ?? false,
  retainUserCredentials: targetEnvironment?.retain_user_credentials ?? false,
  deleteDiskOnTermination: targetEnvironment?.delete_disks_on_vm_termination ?? false,
  allowLiveResize: targetEnvironment?.allow_live_resize ?? false,
  machineType: targetEnvironment?.machine_type ?? '',
  keypairName: targetEnvironment?.keypair_name ?? '',
  floatingIpPool: targetEnvironment?.floating_ip_pool ?? ''
});

export const mapTargetMappings = (
  networkMapping: SchemaMigration['network_mapping'],
  storageMapping: SchemaMigration['storage_mapping']
): MigrationTargetMapping => ({
  networkMappings: networkMapping
    ? Object.entries(networkMapping).map(([source, target]) => ({
      source,
      target: target.id ?? ''
    }))
    : [],
  storageDefault: storageMapping?.default ?? ''
});

const mapper = (m: SchemaMigration): MappedMigration => {
  const instances = m.instances ?? [];
  const { projectId, domainId, domain, project } = resolveProjectDisplay(m.project_id, m.target_project);

  const item: Migration = {
    id: m.id ?? '',
    name: m.name ?? instances.map(i => i.name).filter(Boolean).join(', '),
    status: (m.status as MigrationStatus) ?? MigrationStatus.UNEXECUTED,
    sourceCloudId: m.source_cloud_id ?? '',
    projectId,
    domainId,
    domain,
    project,
    createdAt: m.created_at ?? '',
    updatedAt: m.updated_at ?? '',
    sourceOptions: mapSourceOptions(instances, m.source_environment),
    targetOptions: mapTargetOptions(m.target_environment),
    sourceVMs: instances.map(mapInstance),
    targetMappings: mapTargetMappings(m.network_mapping, m.storage_mapping),
    deploymentOpts: {
      cloneDisks: m.deployment_options?.clone_disks ?? false,
      forceDeployment: m.deployment_options?.force_deployment ?? false,
      osMorphing: m.deployment_options?.os_morphing ?? false,
      saveSnapshot: m.deployment_options?.save_snapshot ?? false,
      start: m.deployment_options?.start ?? false
    }
  };

  return {
    ...item,
    statusText: MIGRATION_STATUS_TEXT[item.status],
    statusIcon: MIGRATION_STATUS_ICON[item.status]
  };
};

export default mapper;
